<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../state/store"
import useStores from "../../use/useStores"
import useDebouncedFunction from "../../use/useDebouncedFunction"

const stores = computed(() => store.state.stores)

const { storeStatus, search, perPage, getStores } = useStores()
const { handleInput } = useDebouncedFunction(getStores)

const formattedPrices = computed(() => {
  return stores.value.data.map((store) =>
    new Intl.NumberFormat("fa-IR").format(store.totalPrice)
  )
})

const emit = defineEmits(["clickEdit", "clickSettlement"])

onMounted(() => {
  getStores()
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getStores(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editStore = (sto) => {
  emit("clickEdit", sto)
}

const settlement = (sto) => {
  emit("clickSettlement", sto)
}
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی فروشگاه، نام، ایمیل و..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getStores(null)"
              v-model="storeStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت فروشگاه</option>
              <option value="0">غیرفعال</option>
              <option value="1">فعال</option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getStores(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="storeTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="store_name">نام</th>
              <th class="sort" data-sort="email">ایمیل</th>
              <th class="sort" data-sort="phone">تلفن</th>
              <th class="sort" data-sort="date">تاریخ ثبت</th>
              <th class="sort" data-sort="status">وضعیت</th>
              <th class="sort" data-sort="creditor">بستانکار</th>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>
          <tbody class="list" v-if="stores.loading || !stores.data.length">
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="stores.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ فروشگاهی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(store, index) of stores.data" :key="index">
              <td class="store_name">
                <router-link
                  :to="{
                    name: 'stores.details',
                    params: { id: store.id },
                  }"
                  class="link-body-emphasis"
                  >{{ store.name }}
                </router-link>
              </td>
              <td class="email">{{ store.email }}</td>
              <td class="phone">
                {{ persianTools.digitsEnToFa(store.phone) }}
              </td>
              <td class="date">{{ toPersianDateTime(store.created_at) }}</td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': store.status == 1,
                    'badge-soft-danger': store.status == 0,
                  }"
                  >{{ store.status ? "فعال" : "غیرفعال" }}</span
                >
              </td>
              <td class="creditor">
                <router-link
                  v-if="store.totalPrice > 0"
                  :to="{
                    name: 'store.creditor.details',
                    params: { storeId: store.id },
                  }"
                  class="link-danger"
                  >{{ formattedPrices[index] }}
                </router-link>
                <span v-else>{{ formattedPrices[index] }}</span>
              </td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="مشاهده لیست فروش ها"
                  >
                    <RouterLink
                      :to="{
                        name: 'store.products.sales',
                        params: { storeId: store.id },
                      }"
                      class="text-primary d-inline-block"
                      ><i class="ri-shopping-bag-3-fill fs-16"></i>
                    </RouterLink>
                  </li>
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="ویرایش"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editStore(store)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>

                  <li
                    v-if="store.totalPrice > 0"
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="تسویه حساب"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="settlement(store)"
                    >
                      <i class="ri-bank-card-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="stores.total > stores.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of stores.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
